import React, { PureComponent } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Spinner } from '@el8/vital';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { mutateAsync, querySelectors } from 'redux-query';
import { connectRequest } from 'redux-query-react';
import {
  fetchAppointmentQuery,
  confirmAppointmentQuery,
  cancelAppointmentQuery,
  getAppointment,
} from 'modules/appointment';
import { getPhysician } from 'modules/physician';
import { getPractice } from 'modules/practice';
import { getAppointmentType } from 'modules/appointmentType';

import { State, PhysicianInfo, PracticeInfo, RQGenericResponse, Dispatch } from 'types';
import { Appointment, AppointmentType } from 'EntityTypes';

import AppointmentAlreadyCanceled from './AppointmentAlreadyCanceled';
import AppointmentConfirmed from './AppointmentConfirmed';
import AppointmentCanceled from './AppointmentCanceled';
import AppointmentManager from './AppointmentManager';
import Box from '../../components/Box/Box';
import styles from '../booking/Practice/Practice.less';

type OwnProps = RouteComponentProps<{
  appointmentId: number;
  token: string;
}>;

type StateProps = {
  appointment?: Appointment;
  physician?: PhysicianInfo;
  practice?: PracticeInfo;
  appointmentType?: AppointmentType;
  isPending?: boolean;
  token: string;
};

type DispatchProps = {
  confirmAppointment: (appointmentId: number, token: string) => Promise<RQGenericResponse>;
  cancelAppointment: (appointmentId: number, token: string) => Promise<RQGenericResponse>;
};

type Props = {} & OwnProps & StateProps & DispatchProps;

const mapStateToProps = (
  state: State,
  { appointmentId, location: { search } }: OwnProps,
): StateProps => {
  const appointment = getAppointment(state, { appointmentId });
  const params = new URLSearchParams(search);
  const token = params.get('token');
  if (!appointment) return { token };

  const physician = getPhysician(state, { physicianId: appointment.physician });
  const appointmentType = getAppointmentType(state, {
    appointmentTypeId: appointment.apptType,
  });

  const practice = getPractice(state, { practiceId: appointment.practice });

  const isPending =
    querySelectors.isPending(state.queries, confirmAppointmentQuery(appointmentId, token)) ||
    querySelectors.isPending(state.queries, cancelAppointmentQuery(appointmentId, token)) ||
    querySelectors.isPending(state.queries, fetchAppointmentQuery(appointmentId, token));

  return {
    appointment,
    physician,
    appointmentType,
    practice,
    isPending,
    token,
  };
};

const mapPropsToQueries = ({ appointmentId, token }: OwnProps) => {
  if (token) return fetchAppointmentQuery(appointmentId, token);

  return null;
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  confirmAppointment: (apptId: number, token: string): Promise<RQGenericResponse> =>
    dispatch(mutateAsync(confirmAppointmentQuery(apptId, token))),
  cancelAppointment: (apptId: number, token: string): Promise<RQGenericResponse> =>
    dispatch(mutateAsync(cancelAppointmentQuery(apptId, token))),
});

class AppointmentManagerContainer extends PureComponent<Props> {
  // cancelActionPerformed allow to differentiate whether the user just cancelled the appointment vs the appointment was already cancelled when the user got to this screen.
  // we can not rely on redux-query isPending for queries performed on componendDidMount, fot that reason we have the isLoading state property.
  state = { cancelActionPerformed: false, isLoading: false };

  componentDidUpdate(prevProps: Props): void {
    if (!prevProps.appointment && this.props.appointment) {
      const params = new URLSearchParams(window.location.search);
      if (this.props.appointment.status !== 'cancelled') {
        if (params.get('confirm')) this.handleConfirmAppointmentClick();
        else if (params.get('cancel')) this.handleCancelAppointmentClick();
      }
    }
  }

  handleConfirmAppointmentClick = async (): Promise<void> => {
    const {
      appointmentId,
      confirmAppointment,
      location: { search },
    } = this.props;
    const params = new URLSearchParams(search);
    this.setState({ isLoading: true }, async (): Promise<void> => {
      try {
        await confirmAppointment(appointmentId, params.get('token'));
      } finally {
        this.setState({ isLoading: false });
      }
    });
  };

  handleCancelAppointmentClick = async (): Promise<void> => {
    const {
      appointmentId,
      cancelAppointment,
      location: { search },
    } = this.props;
    const params = new URLSearchParams(search);
    this.setState({ isLoading: true, cancelActionPerformed: true }, async (): Promise<void> => {
      try {
        await cancelAppointment(appointmentId, params.get('token'));
      } finally {
        this.setState({ isLoading: false });
      }
    });
  };

  render(): JSX.Element {
    const { appointment, appointmentType, physician, practice, isPending } = this.props;
    const { cancelActionPerformed, isLoading } = this.state;

    if (isPending || isLoading)
      return (
        <Box className={styles.loaderContainer} align="center" justify="center">
          <Spinner />
        </Box>
      );

    if (!appointment) return null;
    if (appointment.status === 'scheduled' || !appointment.status)
      return (
        <AppointmentManager
          appointment={appointment}
          physician={physician}
          appointmentType={appointmentType}
          onConfirmAppointment={this.handleConfirmAppointmentClick}
          onCancelAppointment={this.handleCancelAppointmentClick}
          practice={practice}
        />
      );

    if (appointment.status === 'confirmed')
      return (
        <AppointmentConfirmed
          appointment={appointment}
          physician={physician}
          appointmentType={appointmentType}
          practice={practice}
        />
      );

    if (appointment.status === 'cancelled' && cancelActionPerformed)
      return (
        <AppointmentCanceled
          appointment={appointment}
          physician={physician}
          appointmentType={appointmentType}
          practice={practice}
        />
      );

    if (appointment.status === 'cancelled' && !cancelActionPerformed)
      return <AppointmentAlreadyCanceled appointment={appointment} practice={practice} />;

    return null;
  }
}

export default compose<React.ComponentType<OwnProps>>(
  connect(mapStateToProps, mapDispatchToProps),
  connectRequest(mapPropsToQueries),
)(AppointmentManagerContainer);
